/**
 * Step 2: Conversion Details
 *
 * User configures the conversion name, value, and counting settings.
 */
import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  CountingType,
  ConversionFormErrors,
  COUNTING_OPTIONS,
} from '../types';

interface ConversionDetailsStepProps {
  name: string;
  defaultValue: string;
  useDefaultValue: boolean;
  countingType: CountingType;
  onChangeName: (name: string) => void;
  onChangeDefaultValue: (value: string) => void;
  onChangeUseDefaultValue: (use: boolean) => void;
  onChangeCountingType: (type: CountingType) => void;
  errors?: ConversionFormErrors;
}

export const ConversionDetailsStep: React.FC<ConversionDetailsStepProps> = ({
  name,
  defaultValue,
  useDefaultValue,
  countingType,
  onChangeName,
  onChangeDefaultValue,
  onChangeUseDefaultValue,
  onChangeCountingType,
  errors,
}) => {
  return (
    <div className="space-y-6">
      {/* Step introduction */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Conversion Details
        </h3>
        <p className="text-sm text-gray-600">
          Configure how this conversion should be tracked and counted.
        </p>
      </div>

      {/* Conversion Name */}
      <div>
        <label
          htmlFor="conversion-name"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Conversion Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="conversion-name"
          type="text"
          value={name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g., Website Purchase"
          maxLength={100}
          error={errors?.name}
        />
        {errors?.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          A descriptive name to identify this conversion in reports
        </p>
      </div>

      {/* Conversion Value */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Conversion Value
        </label>

        <div className="space-y-3">
          <label
            className={cn(
              'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
              'hover:bg-gray-50',
              {
                'border-blue-600 bg-blue-50': !useDefaultValue,
                'border-gray-200': useDefaultValue,
              }
            )}
          >
            <input
              type="radio"
              name="valueType"
              checked={!useDefaultValue}
              onChange={() => onChangeUseDefaultValue(false)}
              className="mt-0.5"
            />
            <div className="ml-3">
              <p className="font-medium text-gray-900">
                Use different values for each conversion
              </p>
              <p className="text-sm text-gray-500">
                Value will be passed dynamically from your website
              </p>
            </div>
          </label>

          <label
            className={cn(
              'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
              'hover:bg-gray-50',
              {
                'border-blue-600 bg-blue-50': useDefaultValue,
                'border-gray-200': !useDefaultValue,
              }
            )}
          >
            <input
              type="radio"
              name="valueType"
              checked={useDefaultValue}
              onChange={() => onChangeUseDefaultValue(true)}
              className="mt-0.5"
            />
            <div className="ml-3 flex-1">
              <p className="font-medium text-gray-900">
                Use the same value for each conversion
              </p>
              <p className="text-sm text-gray-500 mb-2">
                Set a fixed value for all conversions
              </p>
              {useDefaultValue && (
                <div className="flex items-center">
                  <span className="text-gray-500 mr-2">$</span>
                  <Input
                    type="number"
                    value={defaultValue}
                    onChange={(e) => onChangeDefaultValue(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-32"
                    error={errors?.defaultValue}
                  />
                </div>
              )}
              {errors?.defaultValue && useDefaultValue && (
                <p className="mt-1 text-sm text-red-600">{errors.defaultValue}</p>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Counting Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          How to Count Conversions
        </label>

        <div className="space-y-3">
          {COUNTING_OPTIONS.map((option) => {
            const isSelected = countingType === option.value;

            return (
              <label
                key={option.value}
                className={cn(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="countingType"
                  value={option.value}
                  checked={isSelected}
                  onChange={() => onChangeCountingType(option.value)}
                  className="mt-0.5"
                />
                <div className="ml-3 flex-1">
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConversionDetailsStep;
